import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Download,
  Search,
  RefreshCw,
  FolderOpen,
  Trash2,
} from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import DocumentViewerDialog from "@/components/DocumentViewerDialog";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-ES");
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-700",
    processing: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    failed: "bg-rose-100 text-rose-700",
  };

  const labels = {
    pending: "Pendiente",
    processing: "Procesando",
    completed: "Procesado",
    failed: "Error",
  };

  return (
    <Badge className={styles[status] || styles.pending} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    try {
      const response = await apiFetch(`${API}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await apiFetch(`${API}/documents/upload`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          toast.success(`${file.name} subido correctamente`);
        } else {
          // El backend explica el rechazo (imagen sin consentimiento IA,
          // formato no soportado...) — mostrarlo tal cual.
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || `Error al subir ${file.name}`, { duration: 8000 });
        }
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploading(false);
    fetchDocuments();
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDownload = async (doc) => {
    try {
      const response = await apiFetch(`${API}/documents/${doc.document_id}/download`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Eliminar "${doc.filename}"?`)) return;
    try {
      const response = await apiFetch(`${API}/documents/${doc.document_id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        toast.success("Documento eliminado");
        fetchDocuments();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredDocuments = documents.filter(
    (doc) =>
      !search ||
      doc.filename.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Documentos
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {documents.length} documentos almacenados
          </p>
        </div>
        <div className="flex gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(Array.from(e.target.files || []))}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
            className="hidden"
            multiple
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-document-btn"
          >
            {uploading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Subir Documentos
          </Button>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        className={`upload-area ${dragOver ? "dragover" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        data-testid="documents-drop-zone"
      >
        <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p className="text-slate-600 font-medium">
          Arrastra documentos aquí o haz clic para seleccionar
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Soporta PDF, PNG, JPG, WEBP y HEIC. Las imágenes y los PDFs
          escaneados requieren las funciones de IA activadas en Ajustes.
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar documentos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-documents-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Archivo</TableHead>
                <TableHead className="w-[100px]">Tipo</TableHead>
                <TableHead className="w-[100px]">Tamaño</TableHead>
                <TableHead className="w-[120px]">Fecha</TableHead>
                <TableHead className="w-[100px]">OCR</TableHead>
                <TableHead className="w-[100px]">Vinculado</TableHead>
                <TableHead className="w-[130px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay documentos</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((doc) => (
                  <TableRow key={doc.document_id} data-testid={`doc-row-${doc.document_id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="truncate max-w-[200px]">{doc.filename}</span>
                        {doc.source === "email" && (
                          <Badge
                            variant="secondary"
                            className="text-[10px]"
                            title={`${doc.source_meta?.from || ""} · ${doc.source_meta?.subject || ""}`}
                          >
                            Correo
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="uppercase text-xs text-slate-500">
                      {doc.file_type}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatFileSize(doc.file_size)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatDate(doc.created_at)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={doc.ocr_status} />
                    </TableCell>
                    <TableCell>
                      {doc.linked_type ? (
                        <Badge variant="outline" className="text-xs">
                          {doc.linked_type === "invoice_received" ? "Factura" : doc.linked_type}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <DocumentViewerDialog doc={doc} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          title="Descargar"
                          data-testid={`download-doc-${doc.document_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar"
                          data-testid={`delete-doc-${doc.document_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
